import { useEffect, useRef, useState } from "react";
import { FiPlay } from "react-icons/fi";
import type { Clip } from "./faqClips";

const prefersReducedMotion = () =>
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * A short, silent phone recording in a phone-shaped frame. It plays while
 * on screen and pauses when scrolled away; with reduced motion it waits for
 * the visitor to press play.
 */
const FaqVideo = ({ clip }: { clip: Clip }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [manual] = useState(prefersReducedMotion);

  useEffect(() => {
    const video = ref.current;
    if (!video || manual || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.5 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [manual]);

  return (
    <figure className="mx-auto w-full max-w-[220px]">
      <div className="overflow-hidden rounded-[1.6rem] border-4 border-slate-800 bg-black shadow-xl shadow-black/40">
        <video
          ref={ref}
          src={clip.src}
          poster={clip.poster}
          muted
          loop
          playsInline
          preload="metadata"
          controls={manual}
          aria-label={clip.title}
          className="block aspect-[480/980] w-full object-cover"
        />
      </div>
      <figcaption className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
        <FiPlay aria-hidden className="shrink-0" size={12} />
        {clip.title}
      </figcaption>
    </figure>
  );
};

export default FaqVideo;
